import {
  Disclaimer,
  Div,
  FinancesGamesPlayerEntity,
  H5,
  P,
  Pill,
  TooltipWrapper,
  formatDate,
  lightTheme,
} from '@vgrubs/components';
import {
  PhoneCardDetails,
  PhoneCardLabel,
} from '../../../../Receivables/components/PhoneCardDetails';
import { Cell, NameLine, PlayerRow } from './GamesFinances.styled';
import { count, maskPhone, plural, usd } from './gamesFormat';

/**
 * Underlined in the text's own colour, like the location names on the
 * Orders and Receivables lists. Opens in a new tab, so the Finances tab
 * keeps its range and page.
 */
const NewTabLink = ({ href, children }: { href: string; children: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    style={{ color: 'inherit', textDecoration: 'underline' }}
  >
    {children}
  </a>
);

const BannedTag = ({ bannedAt }: { bannedAt: string }) => (
  <TooltipWrapper tooltip={`Banned for fraud on ${formatDate(bannedAt)}`}>
    <Pill
      $background={lightTheme.transparentRed}
      $minWidth="0"
      $minWidthSM="0"
      $height="20px"
      $padding="0 8px"
      // Block-level flex, as on the Inactive tag: an inline pill in the
      // tooltip's link sits on a text baseline.
      style={{ flex: 'none', display: 'flex' }}
    >
      <Disclaimer $m="0" $bold $noWrap $color={lightTheme.primary}>
        Banned
      </Disclaimer>
    </Pill>
  </TooltipWrapper>
);

/** The restaurant of the player's latest delivery, its brand and order. */
const DeliveryLine = ({ player }: { player: FinancesGamesPlayerEntity }) => {
  if (player.locationId == null)
    return (
      <P $m="0" $color={lightTheme.lightGray}>
        No delivery linked
      </P>
    );
  const otherBrand =
    player.brandName && player.brandName !== player.locationName;
  return (
    <P $m="0">
      <NewTabLink href={`/locations/${player.locationId}`}>
        {player.locationName ?? `Location #${player.locationId}`}
      </NewTabLink>
      {otherBrand && ` · ${player.brandName} (brand)`}
      {player.orderId != null && (
        <>
          {' · '}
          <NewTabLink href={`/orders/${player.orderId}`}>
            {`Order #${player.orderId}`}
          </NewTabLink>
        </>
      )}
    </P>
  );
};

export const GAMES_COLUMNS = [
  { label: 'Player', width: '40%' },
  { label: 'MyChips paid', width: '13%' },
  { label: 'Player got', width: '13%' },
  { label: 'Our share', width: '13%' },
  { label: 'Points balance', width: '13%' },
];

const [PLAYER, ...AMOUNTS] = GAMES_COLUMNS.map((column) => column.width);

const AmountCell = ({
  index,
  children,
  color,
}: {
  /** Of the four amount columns. */
  index: number;
  children: string;
  color?: string;
}) => (
  <Cell $width={AMOUNTS[index]}>
    <H5 $m="0" $color={color}>
      {children}
    </H5>
  </Cell>
);

const shareColor = (cents: number) =>
  cents < 0 ? lightTheme.primary : lightTheme.green;

const balanceColor = (points: number) =>
  points < 0 ? lightTheme.primary : undefined;

const PlayerName = ({ player }: { player: FinancesGamesPlayerEntity }) => (
  <NameLine>
    <H5 $m="0" $color={player.customerName ? undefined : lightTheme.lightGray}>
      {player.customerName ?? 'Unknown customer'}
    </H5>
    <P $m="0" $color={lightTheme.lightGray} $noWrap>
      {maskPhone(player.phoneNumber)}
    </P>
    {player.bannedAt && <BannedTag bannedAt={player.bannedAt} />}
  </NameLine>
);

/**
 * A player on a phone, laid out like the Outstanding Balances cards: our
 * share top right, across from the name, then a column of labels with the
 * values lined up beside it.
 */
const PlayerCard = ({
  player,
  played,
}: {
  player: FinancesGamesPlayerEntity;
  played: string;
}) => (
  <Div $mobile $width="100%" $minWidth="0">
    <Div $flex $alignStart $between $gap="16px">
      <Div $minWidth="0">
        <PlayerName player={player} />
      </Div>
      {/* Never shrinks: the name is the part that wraps. */}
      <Div $flex $column $alignEnd style={{ flex: 'none' }}>
        <H5 $m="0" $color={shareColor(player.ourShare)}>
          {usd(player.ourShare)}
        </H5>
        <Disclaimer $m="0" $noWrap $color={lightTheme.lightGray}>
          Our share
        </Disclaimer>
      </Div>
    </Div>

    <PhoneCardDetails>
      <PhoneCardLabel>Delivery</PhoneCardLabel>
      <DeliveryLine player={player} />

      <PhoneCardLabel>Played</PhoneCardLabel>
      <P $m="0">{played}</P>

      <PhoneCardLabel>MyChips paid</PhoneCardLabel>
      <P $m="0">{usd(player.revenue)}</P>

      <PhoneCardLabel>Player got</PhoneCardLabel>
      <P $m="0">{usd(player.playersShare)}</P>

      <PhoneCardLabel>Points balance</PhoneCardLabel>
      <P $m="0" $color={balanceColor(player.balance)}>
        {count(player.balance)}
      </P>
    </PhoneCardDetails>
  </Div>
);

/**
 * One player: who they are, where their latest delivery came from, what they
 * played, then what MyChips paid for them and how it split.
 */
export const GamesPlayerRow = ({
  player,
}: {
  player: FinancesGamesPlayerEntity;
}) => {
  const played = [
    player.games.join(', '),
    plural(player.conversions, 'task'),
    player.deductions > 0 && `${count(player.deductions)} taken back`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <PlayerRow>
      <Cell $width={PLAYER}>
        <PlayerName player={player} />
        <DeliveryLine player={player} />
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          {played}
        </Disclaimer>
      </Cell>
      <AmountCell index={0}>{usd(player.revenue)}</AmountCell>
      <AmountCell index={1}>{usd(player.playersShare)}</AmountCell>
      <AmountCell index={2} color={shareColor(player.ourShare)}>
        {usd(player.ourShare)}
      </AmountCell>
      <AmountCell index={3} color={balanceColor(player.balance)}>
        {count(player.balance)}
      </AmountCell>

      <PlayerCard player={player} played={played} />
    </PlayerRow>
  );
};
